import { ExtractLetters, GetLinerId, StripLastSegment } from "../editor-utils/id-string-utils"
import { AppendSurroundingPaths } from "../helpers/holes-helper"
import { SetLinerAtributes } from "../helpers/liners-helper"
import { fetchSvgImageFromUrl, RemoveUnListedClasses, ResetSelectedPath, SvgHandler } from "../helpers/svg-helpers"
import { CssSVGClasses, EditorSteps } from "../editor-utils/editor-types"
import asEditorStore from "./../local-store/asset-section-editor-store"
import { ToastLevel } from "@metsooutotec/modes-react-components"
import linerLocationsStore from "features/organizations/asset-sections/store/liner-locations-store"

interface ProcessSubmitEditBoxProps {
    setMessage: (message: string, level?: ToastLevel, timeout?: number) => void
    assetSectionId: string
}
export const ProcessSubmitEditBox = async ({ assetSectionId, setMessage }: ProcessSubmitEditBoxProps): Promise<void> => {
    if (!asEditorStore.selectedPathId) return

    const extractedLettersFromInput = ExtractLetters(asEditorStore.editBoxInputValue as string)
    const { svgDocument } = await fetchSvgImageFromUrl(asEditorStore.assetSectionImage as string)

    let reference = ""
    let pathElement: SVGPathElement | null = null // Initialize as null

    switch (asEditorStore.currentStep) {
        case EditorSteps.LinersLocation:
            // if a Liner has the save value as the edit box don't complete.
            if (asEditorStore.firstLinerName === "" || asEditorStore.firstLinerName !== extractedLettersFromInput)
                asEditorStore.setFirstLinerName(extractedLettersFromInput)

            reference = StripLastSegment(asEditorStore.selectedPathId) as string
            if (reference) {
                const inpurtValue = (asEditorStore.editBoxInputValue as string).trim()

                //// if a Liner With a same reference exist don't complete.
                //if (reference.trim().toLowerCase() === inpurtValue.toLowerCase()) {
                //    setMessage("The Liner reference match the input value!", ToastLevel.WARNING,8000);
                //    return;
                //}

                const existLinerLocations = linerLocationsStore.GetLinerLocationsByAssetSectionId(assetSectionId)

                pathElement = svgDocument.querySelector(`path[id="${inpurtValue}"]`) as SVGPathElement

                let referenceId: string = reference
                const existLinerLocationInSVG = pathElement?.id.trim().toLowerCase() === inpurtValue.toLowerCase() // if the input is already defined in the svg.
                const existLinerLocationInBackEnd = existLinerLocations.find(l => l.reference.trim().toLowerCase() === inpurtValue.toLowerCase()) // if the input is already exist in the backend
                if (existLinerLocationInSVG && existLinerLocationInBackEnd) {
                    setMessage("The Input value is already reserved in the svg!", ToastLevel.WARNING, 8000)
                    return
                } else if (existLinerLocationInBackEnd && existLinerLocationInSVG) {
                    setMessage("The Input value is already reserved in the svg and in backend!", ToastLevel.WARNING, 8000)
                    return
                } else if (existLinerLocationInBackEnd && !existLinerLocationInSVG) {
                    //TODO:: see if this is a good way to handle it.

                    referenceId = inpurtValue
                }

                pathElement = svgDocument.querySelector(`path[id="${reference}"]`) as SVGPathElement
                RemoveUnListedClasses(pathElement)
                pathElement.classList.add(CssSVGClasses.WsLiner)

                // Check and set stroke-width if not set or 0
                SetLinerAtributes(pathElement, inpurtValue, asEditorStore.assetSectionSettings?.lineWeight)

                const linerLocationId = linerLocationsStore.GetLinerLocationIdByReference(assetSectionId, referenceId || inpurtValue) ?? undefined
                const updatedList = asEditorStore.selectedLiners.filter(liner => liner.existReference !== pathElement?.id)
                asEditorStore.setSelectedLiners(
                    [...updatedList, { id: linerLocationId, existReference: reference, newReference: inpurtValue }].sort((a, b) =>
                        a?.newReference?.localeCompare(b?.newReference),
                    ),
                )
            }
            break

        case EditorSteps.WearReferencePlane:
            reference = StripLastSegment(asEditorStore.selectedPathId) as string
            if (reference) {
                pathElement = svgDocument.querySelector(`path[id="${reference}"]`) as SVGPathElement
                RemoveUnListedClasses(pathElement)
                pathElement.classList.add(CssSVGClasses.WsWearReferencePlaneTemp)
                const updatedList = asEditorStore.selectedWearReferencePlane.filter(liner => liner.reference !== reference)
                const linerLocationId = linerLocationsStore.GetLinerLocationIdByReference(assetSectionId, reference) ?? ""
                asEditorStore.setSelectedWearReferencePlane(
                    [
                        ...updatedList,
                        { linerLocationId: linerLocationId, reference: reference, wallThickness: asEditorStore.editBoxInputValue as number },
                    ].sort((a, b) => a?.reference?.localeCompare(b?.reference)),
                )
            }
            break

        case EditorSteps.HolesLocation:
            reference = StripLastSegment(asEditorStore.selectedPathId) as string
            if (reference) {
                const linerLocationId = linerLocationsStore.GetLinerLocationIdByReference(assetSectionId, asEditorStore.surroundingHoleLiner) ?? ""
                const linerLocation = linerLocationsStore.GetLinerLocationById(linerLocationId)
                if (!linerLocation) {
                    setMessage("Surrounded LinerLocation not defined!", ToastLevel.WARNING, 8000)
                    return
                }
                const newReference = `${asEditorStore.surroundingHoleLiner}-Region${asEditorStore.editBoxInputValue}-Hole1`
                if (!reference) {
                    setMessage("Exist Liner Region Reference Not been Found in the store!", ToastLevel.WARNING, 8000)
                    return
                }

                pathElement = svgDocument.querySelector(`path[id="${reference}"]`) as SVGPathElement
                RemoveUnListedClasses(pathElement)

                const isDefined = pathElement.classList.contains(CssSVGClasses.WsHole)
                if (!isDefined) pathElement.classList.add(CssSVGClasses.WsHole)

                pathElement.setAttribute("id", newReference)

                const storedRegion = asEditorStore.selectedholes.find(hole => hole.newReference === reference)
                const updatedHolesList = asEditorStore.selectedholes.filter(hole =>
                    storedRegion ? hole.newReference !== reference : hole.existReference !== reference,
                )
                const existLinerRegions = linerLocationsStore.GetLinerRegionByLinerRegionRef(linerLocationId, reference)

                asEditorStore.setSelectedHoles(
                    [
                        ...updatedHolesList,
                        {
                            linerLocationId: linerLocationId,
                            id: storedRegion?.id ?? existLinerRegions?.id,
                            newName: asEditorStore.editBoxLinerRegionNameInputValue ?? storedRegion?.newName ?? "",
                            existName: storedRegion?.newName ?? "",
                            existReference: reference,
                            newReference: newReference,
                        },
                    ].sort((a, b) => a?.newReference?.localeCompare(b?.newReference)),
                )
                AppendSurroundingPaths(
                    document,
                    asEditorStore.selectedPathId,
                    pathElement,
                    asEditorStore.editAssetSectionWindow.sensorNotificationArea,
                    reference,
                )
            }
            break

        case EditorSteps.LinersDrawingReferences:
            if (asEditorStore.firstDrawingReferenceName === "" || asEditorStore.firstDrawingReferenceName !== extractedLettersFromInput)
                asEditorStore.setFirstDrawingReferenceName(extractedLettersFromInput)
            reference = GetLinerId(asEditorStore.selectedPathId) as string // Assuming we're using the same ID format for references, else create a new helper function.
            if (reference) {
                pathElement = svgDocument.querySelector(`path[id="${reference}"]`) as SVGPathElement
                const linerLocationId = linerLocationsStore.GetLinerLocationIdByReference(assetSectionId, reference) ?? ""
                RemoveUnListedClasses(pathElement)
                pathElement.classList.add(CssSVGClasses.WsReferenceTemp)
                const updatedReferencesList = asEditorStore.selectedLinersDrawingRef.filter(reference => reference.linerReference !== pathElement?.id)
                asEditorStore.setSelectedLinersDrawingRef(
                    [
                        ...updatedReferencesList,
                        {
                            linerLocationId: linerLocationId,
                            linerReference: reference,
                            newDrawingReferenceName: asEditorStore.editBoxInputValue as string,
                        },
                    ].sort((a, b) => a?.newDrawingReferenceName?.localeCompare(b?.newDrawingReferenceName)),
                )
            }
            break
    }

    if (!reference) {
        console.warn(`Path has :${asEditorStore.selectedPathId}, so ${asEditorStore.currentStep.toString().toUpperCase()} could not be found`)
        return
    }
    ResetSelectedPath(asEditorStore.selectedPathId)
    asEditorStore.resetUIStates()

    const blobUrl = SvgHandler.Serialize(svgDocument)
    asEditorStore.setAssetSectionImage(blobUrl)
}
